// The Inventory count in the counter's own language (the packingText.ts pattern: the language is the
// PERSON's preference, Settings → My profile; on a tablet the person chosen in "Who is counting?").
// ET and RU were written by Claude and are to be checked by Fernando. (2026-10-06; the rework's texts 2026-10-09)
import type { Lang } from './packingText';

interface Texts {
  whoCounts: string;
  change: string;
  start: string;
  save: string;
  markDone: string;
  reopen: string;
  prev: string;
  next: string;
  scanPlaceholder: string;
  inFlight: (n: number) => string;
  syncChip: string;
  syncHint: string;
  expected: string;
  counted: string;
  mayBeHere: string;
  countsToward: (home: string) => string;
  shouldntBeHere: string;
  foundElsewhere: string;
  setAside: string;
  fixedByManager: string;
  // scan popups
  correct: string;
  more: string;
  fewer: string;
  wrongPlace: string;
  belongsTo: (home: string) => string;
  overflowPopup: string;
  nosCounted: string;
  unknown: string;
  ambiguous: string;
  // v3 (8.10): the laptop decides, the server records; conclusion before Save; skip / clear
  load: string;
  loadedAt: (t: string, n: number) => string;
  loading: string;
  pendingScans: (n: number) => string;
  offlineWaiting: (n: number) => string;
  checking: string;
  corrected: (from: string, to: string) => string;
  stopped: (reason: string) => string;
  notRecorded: (n: number) => string;
  noHomeShelf: string;
  doubleScan: string;
  readyToFinish: string;
  backToCounting: string;
  saveCount: string;
  nextSection: string;
  workArea: string;
  skip: string;
  unskip: string;
  skipThisSize: (size: string) => string;
  skipAllSizes: (n: number) => string;
  clearAllSizes: (n: number) => string;
  clearProductConfirm: (label: string, n: number) => string;
  clearSection: string;
  clearSectionConfirm: (display: string) => string;
  skippedBy: (who: string) => string;
  // moves (8.10)
  move: string;
  moveBarTitle: (rows: number, pcs: number) => string;
  holds: (n: number) => string;
  otherSection: string;
  otherSectionPlaceholder: string;
  moveNow: (target: string) => string;
  movePartial: (label: string, moving: string, staying: string) => string;
  movePending: (target: string) => string;
  cancelMove: string;
  // row buttons + Move here + the update gate (8.10 afternoon)
  clear: string;
  skipWhich: string;
  moveHere: string;
  moveHome: string;
  moveHereHint: (section: string) => string;
  moveHomeHint: (home: string) => string;
  noHomeToGo: string;
  hereChip: string;
  herePending: (section: string) => string;
  updateInProgress: (mins: number) => string;
  newVersionReload: string;
  // the rework (9.10): RESTOCK tag, Pause / Cancel, moves before scanning, the Shop block, why an item waits
  restockTag: (list: string) => string;
  pause: string;
  resume: string;
  pausedBanner: string;
  cancelCount: string;
  cancelCountConfirm: (display: string) => string;
  cancelled: (display: string) => string;
  openCounts: string;
  openLate: string;
  pausedChip: string;
  prevSection: string;
  targetCounted: (t: string, n: number) => string;
  targetNotCounted: (t: string) => string;
  targetBusy: (t: string, by: string) => string;
  targetReserved: (t: string) => string;
  clearScannedConfirm: (n: number, t: string) => string;
  scanFirst: string;
  movedUnscanned: (t: string) => string;
  notScanned: string;
  shop: { title: string; modeOff: string; modeOn: string; write: string; held: string; skipped: string; pending: string; written: string;
          review: string; nothing: (n: number) => string; infinite: (n: number) => string; counted: string; expected: string; already: string;
          changed: string; later: (n: number, places: string) => string };
  holdText: Record<string, (places: string) => string>;
  summary: Record<string, string>;
  // legend
  legend: Record<string, string>;
}

const T: Record<Lang, Texts> = {
  en: {
    whoCounts: 'Who is counting?',
    change: 'Change',
    start: 'Start counting',
    save: 'Save count',
    markDone: 'Section done',
    reopen: 'Reopen',
    prev: 'Prev',
    next: 'Next',
    scanPlaceholder: 'Scan a barcode…',
    inFlight: n => `${n} piece${n === 1 ? '' : 's'} of this section ${n === 1 ? 'is' : 'are'} in a round being picked right now — the count will not match until that round is done.`,
    syncChip: 'stock from the hourly sync',
    syncHint: 'Shopify could not be read live — Nora quantities are up to an hour old; these items are not written to the shop (recount when Shopify is back).',
    expected: 'Expected',
    counted: 'Counted',
    mayBeHere: 'May be here — restock shelf',
    countsToward: home => `counts toward ${home}`,
    shouldntBeHere: "Shouldn't be here — belongs elsewhere",
    foundElsewhere: 'Belongs here, found in another section',
    setAside: 'Set the piece aside',
    fixedByManager: 'They count where they were found. Move here / Move home when you carry them; what is left a manager decides on the Manager page.',
    correct: '✓ Correct',
    more: 'More than expected',
    fewer: 'Fewer than expected',
    wrongPlace: 'Wrong place',
    belongsTo: home => `belongs to ${home} — set aside`,
    overflowPopup: 'Restock shelf — counted toward home',
    nosCounted: 'Infinite stock (WGR) — counted',
    unknown: 'Unknown barcode — set aside',
    ambiguous: 'Barcode on several garments — set aside, tell a manager',
    load: 'Load the hall map',
    loadedAt: (t, n) => `hall map loaded ${t} · ${n.toLocaleString()} barcodes`,
    loading: 'Loading the hall map…',
    pendingScans: n => `${n} scan${n === 1 ? '' : 's'} saving…`,
    offlineWaiting: n => `offline — ${n} scan${n === 1 ? '' : 's'} waiting, keep scanning`,
    checking: 'Checking…',
    corrected: (from, to) => `Corrected: ${from} → ${to}`,
    stopped: reason => `Counting stopped — ${reason === 'round_closed' ? 'the inventory round was closed' : reason === 'missing' ? 'this session no longer exists' : reason === 'cancelled' ? 'this count was cancelled' : 'this count was saved elsewhere or closed by a manager'}.`,
    notRecorded: n => `${n} scan${n === 1 ? ' may' : 's may'} not have been recorded — check the row${n === 1 ? '' : 's'} after Start`,
    noHomeShelf: 'no home shelf',
    doubleScan: 'double scan ignored',
    readyToFinish: 'Ready to finish',
    backToCounting: '← Back to counting',
    saveCount: 'Save count',
    nextSection: 'Next section ›',
    workArea: 'Work area',
    skip: 'Skip',
    unskip: 'Count after all',
    skipThisSize: size => `Skip this size only (${size || '—'})`,
    skipAllSizes: n => `Skip all ${n} sizes`,
    clearAllSizes: n => `Clear the scans of all ${n} sizes`,
    clearProductConfirm: (label, n) => `Clear every scan of ${label} in this section (${n} size${n === 1 ? '' : 's'})? The scan history stays.`,
    clearSection: 'Clear section',
    clearSectionConfirm: display => `Clear the whole count of ${display}?\n\nEvery counted quantity goes back to 0 and the expected quantities are read again from the shops. The scan history stays. For a quick restart.`,
    skippedBy: who => `skipped by ${who}`,
    move: 'Move',
    moveBarTitle: (rows, pcs) => `Move ${rows} row${rows === 1 ? '' : 's'} · ${pcs} pcs to`,
    holds: n => `holds ${n} pcs`,
    otherSection: 'Other section…',
    otherSectionPlaceholder: 'Type or scan a section code',
    moveNow: target => `Move to ${target}`,
    movePartial: (label, moving, staying) => `Not all sizes of ${label} are moving (${moving} move, ${staying} stay). Are you sure?`,
    movePending: target => `Moving to ${target} — applied when you save the count`,
    cancelMove: 'Cancel move',
    clear: 'Clear',
    skipWhich: 'Skip which sizes?',
    moveHere: 'Move here',
    moveHome: 'Move home',
    moveHereHint: section => `This item lives on ${section} from now on — its shelf is rewritten when you save the count`,
    moveHomeHint: home => `Carry the piece to ${home} — it is added there (or counted there, if ${home} is not counted yet)`,
    noHomeToGo: 'This item has no home shelf — use Move here',
    hereChip: 'lives here',
    herePending: section => `Lives on ${section} from Save count — you can cancel until then`,
    updateInProgress: mins => `Update in progress — check back in about ${mins} minute${mins === 1 ? '' : 's'}. A count that is already running is not affected.`,
    newVersionReload: 'A new version of the WMS is ready — the page reloads now.',
    restockTag: list => `RESTOCK ${list}`,
    pause: 'Pause',
    resume: 'Continue',
    pausedBanner: 'Paused — the counting time is stopped. Scan or press Continue to go on.',
    cancelCount: 'Cancel count',
    cancelCountConfirm: display => `Cancel the count of ${display}?\n\nEverything this count changed is undone — scans, ±, skips, clears and moves made before scanning — and the section goes back to how it was before Start. The scans stay in the history.`,
    cancelled: display => `The count of ${display} was cancelled — nothing of it was kept.`,
    openCounts: 'Sections still being counted',
    openLate: 'Still open after 17:00 — save or cancel before you leave',
    pausedChip: 'paused',
    prevSection: '‹ Previous section',
    targetCounted: (t, n) => `${t} is counted — the ${n} scanned piece${n === 1 ? ' is' : 's are'} added there at Save count`,
    targetNotCounted: t => `${t} is not counted yet — it counts these pieces; the shelf becomes ${t} now`,
    targetBusy: (t, by) => `${t} is being counted by ${by} — move them later`,
    targetReserved: t => `${t} is a reserved hole`,
    clearScannedConfirm: (n, t) => `${n} piece${n === 1 ? ' of these sizes is' : 's of these sizes are'} scanned here. Clear ${n === 1 ? 'it' : 'them'} here and let ${t} count ${n === 1 ? 'it' : 'them'}?`,
    scanFirst: 'scan the pieces first — they are added to a counted section',
    movedUnscanned: t => `Moved to ${t} — ${t} counts them (✕ undoes it while you count)`,
    notScanned: 'not scanned',
    shop: { title: 'Shop', modeOff: 'Writes are OFF — nothing is sent to the shop; this is what would be written.', modeOn: 'Written to the shop when you save the count.',
            write: 'Will be written', held: 'Held — not written yet', skipped: 'Skipped — the shop\'s number is kept', pending: 'Sending…', written: 'Written ✓',
            later: (n, places) => `${n} item${n === 1 ? '' : 's'} finish when ${places || 'their other place'} ${places.includes(',') ? 'are' : 'is'} counted`,
            review: 'Failed — in Review', nothing: n => `${n} item${n === 1 ? '' : 's'} match the shop`, infinite: n => `${n} with infinite stock (WGR) — never written`,
            counted: 'counted', expected: 'expected', already: 'already written', changed: 'The numbers changed since you checked — check them again.' },
    holdText: { not_counted: p => `${p} not counted yet`, counting_now: p => `${p} being counted right now`, skipped: p => `skipped at ${p}`,
                not_live: p => `the shop was not read live when ${p} was saved — save it again`, sync: p => `the count at ${p} started from the hourly sync — recount it`,
                moved: p => `stock changed during the count at ${p} and nothing explains it — recount`,
                two_shelves: p => `stock changed between the counts of ${p} — recount one of them`, gone: () => 'the product row is gone' },
    summary: { title: 'Conclusion', expected: 'Expected', counted: 'Counted', scanned: 'scanned', typed: 'typed', skipped: 'Skipped — stock trusted',
               extra: 'Extra', missing: 'Missing', wrongPlace: 'Wrong items', movedIn: 'Moved in', movedOut: 'Moved out', duration: 'Time',
               nos: 'Infinite stock (WGR) counted', restock: 'On restock shelves', missingRows: 'Missing pieces', extraRows: 'Extra pieces', skippedRows: 'Skipped',
               movedUnscanned: 'Moved before scanning',
               wrongRows: 'Wrong items — belong elsewhere', foundRows: 'Found in another section', moves: 'Moves', movesPending: 'applied at Save count',
               movement: 'Moved between Start and Save', movementOk: 'explained by events', movementBad: 'not explained — recount this item',
               saved: 'Count saved — section done', preview: 'Check before saving', noIssues: 'Everything counted as expected.' },
    legend: { white: 'Not counted', orange: 'Counting', green: 'Exact', blue: 'More than expected', red: 'Fewer than expected',
              yellow: 'Wrong place', purple: 'Missing', skipped: 'Skipped — stock trusted', gray: 'Infinite stock (WGR) / restock shelf — just counted' },
  },
  et: {
    whoCounts: 'Kes loendab?',
    change: 'Muuda',
    start: 'Alusta loendamist',
    save: 'Salvesta loendus',
    markDone: 'Sektsioon valmis',
    reopen: 'Ava uuesti',
    prev: 'Eelmine',
    next: 'Järgmine',
    scanPlaceholder: 'Skaneeri triipkood…',
    inFlight: n => `Selle sektsiooni ${n} ${n === 1 ? 'toode on' : 'toodet on'} praegu korjatavas ringis — loendus ei klapi enne, kui see ring on valmis.`,
    syncChip: 'laoseis tunnisünkist',
    syncHint: 'Shopifyt ei saanud otse lugeda — Nora kogused võivad olla kuni tund vanad; neid tooteid poodi ei kirjutata (loe uuesti, kui Shopify töötab).',
    expected: 'Oodatud',
    counted: 'Loetud',
    mayBeHere: 'Võib olla siin — täiendusriiul (restock)',
    countsToward: home => `läheb koha ${home} arvesse`,
    shouldntBeHere: 'Ei peaks siin olema — kuulub mujale',
    foundElsewhere: 'Kuulub siia, leitud teisest sektsioonist',
    setAside: 'Pane toode kõrvale',
    fixedByManager: 'Need loetakse sinna, kus need leiti. „Liiguta siia“ / „Oma kohale“, kui need ära viid; ülejäänu otsustab juht Manageri lehel.',
    correct: '✓ Õige',
    more: 'Rohkem kui oodatud',
    fewer: 'Vähem kui oodatud',
    wrongPlace: 'Vale koht',
    belongsTo: home => `kuulub kohta ${home} — pane kõrvale`,
    overflowPopup: 'Täiendusriiul (restock) — loetud kodukoha arvesse',
    nosCounted: 'Lõpmatu laoseis (WGR) — loetud',
    unknown: 'Tundmatu triipkood — pane kõrvale',
    ambiguous: 'Triipkood mitmel tootel — pane kõrvale, ütle juhile',
    load: 'Laadi lao kaart',
    loadedAt: (t, n) => `lao kaart laaditud ${t} · ${n.toLocaleString()} triipkoodi`,
    loading: 'Lao kaardi laadimine…',
    pendingScans: n => `${n} skaneeringut salvestub…`,
    offlineWaiting: n => `võrguta — ${n} skaneeringut ootab, skaneeri edasi`,
    checking: 'Kontrollin…',
    corrected: (from, to) => `Parandatud: ${from} → ${to}`,
    stopped: reason => `Loendamine peatus — ${reason === 'round_closed' ? 'inventuuriring suleti' : reason === 'missing' ? 'seda sessiooni enam pole' : reason === 'cancelled' ? 'see loendus tühistati' : 'see loendus salvestati mujal või juht sulges selle'}.`,
    notRecorded: n => `${n} skaneeringut ei pruugi olla salvestatud — kontrolli ridu pärast Alusta`,
    noHomeShelf: 'koduriiul puudub',
    doubleScan: 'topeltskaneering jäeti vahele',
    readyToFinish: 'Valmis lõpetama',
    backToCounting: '← Tagasi loendama',
    saveCount: 'Salvesta loendus',
    nextSection: 'Järgmine sektsioon ›',
    workArea: 'Tööala',
    skip: 'Jäta vahele',
    unskip: 'Loe siiski',
    skipThisSize: size => `Jäta vahele ainult see suurus (${size || '—'})`,
    skipAllSizes: n => `Jäta vahele kõik ${n} suurust`,
    clearAllSizes: n => `Tühjenda kõigi ${n} suuruse skaneeringud`,
    clearProductConfirm: (label, n) => `Tühjendada toote ${label} kõik skaneeringud selles sektsioonis (${n} suurust)? Skaneeringute ajalugu jääb alles.`,
    clearSection: 'Tühjenda sektsioon',
    clearSectionConfirm: display => `Tühjendada kogu sektsiooni ${display} loendus?\n\nKõik loetud kogused lähevad nulli ja oodatud kogused loetakse poodidest uuesti. Skaneeringute ajalugu jääb alles. Kiireks uueks alguseks.`,
    skippedBy: who => `vahele jättis ${who}`,
    move: 'Liiguta',
    moveBarTitle: (rows, pcs) => `Liiguta ${rows} rida · ${pcs} tk kohta`,
    holds: n => `${n} tk riiulil`,
    otherSection: 'Muu sektsioon…',
    otherSectionPlaceholder: 'Kirjuta või skaneeri sektsiooni kood',
    moveNow: target => `Liiguta kohta ${target}`,
    movePartial: (label, moving, staying) => `Mitte kõik toote ${label} suurused ei liigu (${moving} liigub, ${staying} jääb). Kas oled kindel?`,
    movePending: target => `Liigub kohta ${target} — rakendub loenduse salvestamisel`,
    cancelMove: 'Tühista liigutus',
    clear: 'Tühjenda',
    skipWhich: 'Milliseid suurusi vahele jätta?',
    moveHere: 'Liiguta siia',
    moveHome: 'Oma kohale',
    moveHereHint: section => `See toode elab nüüdsest kohas ${section} — riiul kirjutatakse ümber loenduse salvestamisel`,
    moveHomeHint: home => `Vii ese kohta ${home} — see lisatakse sinna (või loetakse seal, kui ${home} pole veel loetud)`,
    noHomeToGo: 'Sellel tootel pole oma riiulit — kasuta „Liiguta siia“',
    hereChip: 'elab siin',
    herePending: section => `Elab kohas ${section} alates salvestamisest — kuni selleni saab tühistada`,
    updateInProgress: mins => `Uuendus käib — proovi uuesti umbes ${mins} minuti pärast. Juba käivat lugemist see ei sega.`,
    newVersionReload: 'WMS-i uus versioon on valmis — leht laeb end uuesti.',
    restockTag: list => `RESTOCK ${list}`,
    pause: 'Paus',
    resume: 'Jätka',
    pausedBanner: 'Paus — loendamise aeg on peatatud. Skaneeri või vajuta „Jätka“.',
    cancelCount: 'Tühista loendus',
    cancelCountConfirm: display => `Tühistada sektsiooni ${display} loendus?\n\nKõik selle loenduse muudatused võetakse tagasi — skaneeringud, ±, vahelejätmised, tühjendused ja enne skaneerimist tehtud liigutused — ning sektsioon on nagu enne alustamist. Skaneeringud jäävad ajalukku.`,
    cancelled: display => `Sektsiooni ${display} loendus tühistati — sellest ei jäänud midagi alles.`,
    openCounts: 'Pooleli loendused',
    openLate: 'Pärast kella 17 veel pooleli — salvesta või tühista enne lahkumist',
    pausedChip: 'paus',
    prevSection: '‹ Eelmine sektsioon',
    targetCounted: (t, n) => `${t} on loetud — ${n} skaneeritud toodet lisatakse sinna salvestamisel`,
    targetNotCounted: t => `${t} pole veel loetud — need tooted loetakse seal; riiuliks saab kohe ${t}`,
    targetBusy: (t, by) => `${t} loendab praegu ${by} — liiguta hiljem`,
    targetReserved: t => `${t} on reserveeritud auk`,
    clearScannedConfirm: (n, t) => `Nende suuruste ${n} toodet on siin skaneeritud. Tühjendada need siin ja lasta neid lugeda kohas ${t}?`,
    scanFirst: 'skaneeri tooted enne — need lisatakse loetud sektsiooni',
    movedUnscanned: t => `Liigutatud kohta ${t} — need loetakse seal (✕ võtab tagasi, kuni loendus käib)`,
    notScanned: 'skaneerimata',
    shop: { title: 'Pood', modeOff: 'Kirjutamine on VÄLJAS — poodi ei saadeta midagi; nii kirjutataks.', modeOn: 'Kirjutatakse poodi loenduse salvestamisel.',
            write: 'Kirjutatakse', held: 'Ootel — veel ei kirjutata', skipped: 'Vahele jäetud — poe laoseis jääb', pending: 'Saadan…', written: 'Kirjutatud ✓',
            later: (n, places) => `${n} toodet lõpetatakse, kui ${places || 'nende teine koht'} on loetud`,
            review: 'Ebaõnnestus — Review lehel', nothing: n => `${n} toodet klapib poega`, infinite: n => `${n} lõpmatu laoseisuga (WGR) — ei kirjutata`,
            counted: 'loetud', expected: 'oodatud', already: 'juba kirjutatud', changed: 'Numbrid muutusid pärast kontrolli — kontrolli uuesti.' },
    holdText: { not_counted: p => `${p} pole veel loetud`, counting_now: p => `${p} loendatakse praegu`, skipped: p => `vahele jäetud: ${p}`,
                not_live: p => `poodi ei loetud ${p} salvestamisel otse — salvesta uuesti`, sync: p => `${p} loendus algas tunnisünkist — loe uuesti`,
                moved: p => `laoseis muutus ${p} loendamise ajal ja midagi ei selgita seda — loe uuesti`,
                two_shelves: p => `laoseis muutus kohtade ${p} loenduste vahel — loe üks neist uuesti`, gone: () => 'toote rida on kadunud' },
    summary: { title: 'Kokkuvõte', expected: 'Oodatud', counted: 'Loetud', scanned: 'skaneeritud', typed: 'käsitsi', skipped: 'Vahele jäetud — laoseisu usaldame',
               extra: 'Üle', missing: 'Puudu', wrongPlace: 'Valed tooted', movedIn: 'Toodud siia', movedOut: 'Viidud ära', duration: 'Aeg',
               nos: 'Lõpmatu laoseis (WGR) loetud', restock: 'Täiendusriiulitel', missingRows: 'Puuduvad tooted', extraRows: 'Ülearused tooted', skippedRows: 'Vahele jäetud',
               movedUnscanned: 'Liigutatud enne skaneerimist',
               wrongRows: 'Valed tooted — kuuluvad mujale', foundRows: 'Leitud teisest sektsioonist', moves: 'Liigutused', movesPending: 'rakendub salvestamisel',
               movement: 'Muutus Alusta ja Salvesta vahel', movementOk: 'sündmustega selgitatud', movementBad: 'selgitamata — loe see toode uuesti',
               saved: 'Loendus salvestatud — sektsioon valmis', preview: 'Kontrolli enne salvestamist', noIssues: 'Kõik loetud nagu oodatud.' },
    legend: { white: 'Lugemata', orange: 'Lugemisel', green: 'Täpne', blue: 'Rohkem kui oodatud', red: 'Vähem kui oodatud',
              yellow: 'Vale koht', purple: 'Puudu', skipped: 'Vahele jäetud — laoseisu usaldame', gray: 'Lõpmatu laoseis (WGR) / täiendusriiul — lihtsalt loetud' },
  },
  ru: {
    whoCounts: 'Кто считает?',
    change: 'Изменить',
    start: 'Начать подсчёт',
    save: 'Сохранить подсчёт',
    markDone: 'Секция готова',
    reopen: 'Открыть снова',
    prev: 'Назад',
    next: 'Далее',
    scanPlaceholder: 'Отсканируйте штрихкод…',
    inFlight: n => `${n} шт. из этой секции сейчас в собираемом раунде — подсчёт не сойдётся, пока этот раунд не завершится.`,
    syncChip: 'остаток из часовой синхронизации',
    syncHint: 'Shopify не удалось прочитать напрямую — количества Nora могут быть до часа давности; эти товары не записываются в магазин (пересчитайте, когда Shopify заработает).',
    expected: 'Ожидается',
    counted: 'Посчитано',
    mayBeHere: 'Может быть здесь — полка пополнения (restock)',
    countsToward: home => `засчитывается в ${home}`,
    shouldntBeHere: 'Не должно быть здесь — относится к другой секции',
    foundElsewhere: 'Относится сюда, найдено в другой секции',
    setAside: 'Отложите товар',
    fixedByManager: 'Они засчитываются там, где найдены. «Перенести сюда» / «На своё место», если вы их переносите; остальное решает менеджер на странице Manager.',
    correct: '✓ Верно',
    more: 'Больше ожидаемого',
    fewer: 'Меньше ожидаемого',
    wrongPlace: 'Не то место',
    belongsTo: home => `относится к ${home} — отложите`,
    overflowPopup: 'Полка пополнения (restock) — засчитано в домашнюю секцию',
    nosCounted: 'Бесконечный остаток (WGR) — посчитано',
    unknown: 'Неизвестный штрихкод — отложите',
    ambiguous: 'Штрихкод на нескольких товарах — отложите, скажите менеджеру',
    load: 'Загрузить карту склада',
    loadedAt: (t, n) => `карта склада загружена ${t} · ${n.toLocaleString()} штрихкодов`,
    loading: 'Загрузка карты склада…',
    pendingScans: n => `${n} скан. сохраняется…`,
    offlineWaiting: n => `нет сети — ${n} скан. ожидает, продолжайте сканировать`,
    checking: 'Проверяю…',
    corrected: (from, to) => `Исправлено: ${from} → ${to}`,
    stopped: reason => `Подсчёт остановлен — ${reason === 'round_closed' ? 'раунд инвентаризации закрыт' : reason === 'missing' ? 'этой сессии больше нет' : reason === 'cancelled' ? 'этот подсчёт отменён' : 'этот подсчёт сохранён в другом месте или закрыт менеджером'}.`,
    notRecorded: n => `${n} скан. могли не записаться — проверьте строки после Старта`,
    noHomeShelf: 'нет домашней полки',
    doubleScan: 'двойной скан пропущен',
    readyToFinish: 'Готово к завершению',
    backToCounting: '← Назад к подсчёту',
    saveCount: 'Сохранить подсчёт',
    nextSection: 'Следующая секция ›',
    workArea: 'Рабочая зона',
    skip: 'Пропустить',
    unskip: 'Всё же посчитать',
    skipThisSize: size => `Пропустить только этот размер (${size || '—'})`,
    skipAllSizes: n => `Пропустить все ${n} размеров`,
    clearAllSizes: n => `Очистить сканы всех ${n} размеров`,
    clearProductConfirm: (label, n) => `Очистить все сканы товара ${label} в этой секции (${n} разм.)? История сканов сохраняется.`,
    clearSection: 'Очистить секцию',
    clearSectionConfirm: display => `Очистить весь подсчёт секции ${display}?\n\nВсе посчитанные количества обнуляются, ожидаемые количества читаются из магазинов заново. История сканов сохраняется. Для быстрого перезапуска.`,
    skippedBy: who => `пропустил(а) ${who}`,
    move: 'Перенести',
    moveBarTitle: (rows, pcs) => `Перенести ${rows} стр. · ${pcs} шт. в`,
    holds: n => `на полке ${n} шт.`,
    otherSection: 'Другая секция…',
    otherSectionPlaceholder: 'Введите или отсканируйте код секции',
    moveNow: target => `Перенести в ${target}`,
    movePartial: (label, moving, staying) => `Не все размеры товара ${label} переносятся (${moving} переносятся, ${staying} остаются). Вы уверены?`,
    movePending: target => `Переносится в ${target} — применится при сохранении подсчёта`,
    cancelMove: 'Отменить перенос',
    clear: 'Очистить',
    skipWhich: 'Какие размеры пропустить?',
    moveHere: 'Перенести сюда',
    moveHome: 'На своё место',
    moveHereHint: section => `Этот товар теперь живёт в ${section} — полка перезапишется при сохранении подсчёта`,
    moveHomeHint: home => `Отнесите вещь в ${home} — она добавится туда (или посчитается там, если ${home} ещё не посчитана)`,
    noHomeToGo: 'У товара нет своей полки — используйте «Перенести сюда»',
    hereChip: 'живёт здесь',
    herePending: section => `Живёт в ${section} после сохранения — до этого можно отменить`,
    updateInProgress: mins => `Идёт обновление — попробуйте снова примерно через ${mins} мин. Уже идущий подсчёт это не затрагивает.`,
    newVersionReload: 'Готова новая версия WMS — страница перезагружается.',
    restockTag: list => `RESTOCK ${list}`,
    pause: 'Пауза',
    resume: 'Продолжить',
    pausedBanner: 'Пауза — время подсчёта остановлено. Сканируйте или нажмите «Продолжить».',
    cancelCount: 'Отменить подсчёт',
    cancelCountConfirm: display => `Отменить подсчёт секции ${display}?\n\nВсё, что изменил этот подсчёт, отменяется — сканы, ±, пропуски, очистки и переносы до сканирования — и секция возвращается в состояние до Старта. Сканы остаются в истории.`,
    cancelled: display => `Подсчёт секции ${display} отменён — ничего из него не сохранено.`,
    openCounts: 'Незавершённые подсчёты',
    openLate: 'Не завершено после 17:00 — сохраните или отмените перед уходом',
    pausedChip: 'пауза',
    prevSection: '‹ Предыдущая секция',
    targetCounted: (t, n) => `${t} уже посчитана — ${n} отсканированных шт. добавятся туда при сохранении`,
    targetNotCounted: t => `${t} ещё не посчитана — там их посчитают; полка товара сразу становится ${t}`,
    targetBusy: (t, by) => `${t} сейчас считает ${by} — перенесите позже`,
    targetReserved: t => `${t} — зарезервированное место`,
    clearScannedConfirm: (n, t) => `${n} шт. этих размеров уже отсканированы здесь. Очистить их здесь, чтобы их посчитали в ${t}?`,
    scanFirst: 'сначала отсканируйте — они добавятся в посчитанную секцию',
    movedUnscanned: t => `Перенесено в ${t} — там их посчитают (✕ отменяет, пока идёт подсчёт)`,
    notScanned: 'не отсканировано',
    shop: { title: 'Магазин', modeOff: 'Запись ВЫКЛЮЧЕНА — в магазин ничего не отправляется; вот что было бы записано.', modeOn: 'Записывается в магазин при сохранении подсчёта.',
            write: 'Будет записано', held: 'Ожидает — пока не записывается', skipped: 'Пропущено — остаток магазина сохраняется', pending: 'Отправляется…', written: 'Записано ✓',
            later: (n, places) => `${n} товаров завершатся, когда будет посчитано: ${places || 'их другое место'}`,
            review: 'Ошибка — в Review', nothing: n => `${n} товаров совпадают с магазином`, infinite: n => `${n} с бесконечным остатком (WGR) — не записываются`,
            counted: 'посчитано', expected: 'ожидалось', already: 'уже записано', changed: 'Цифры изменились после проверки — проверьте снова.' },
    holdText: { not_counted: p => `${p} ещё не посчитана`, counting_now: p => `${p} сейчас считается`, skipped: p => `пропущено в ${p}`,
                not_live: p => `при сохранении ${p} магазин не был прочитан напрямую — сохраните снова`, sync: p => `подсчёт ${p} начат с часовой синхронизации — пересчитайте`,
                moved: p => `остаток изменился во время подсчёта ${p}, и ничто этого не объясняет — пересчитайте`,
                two_shelves: p => `остаток изменился между подсчётами ${p} — пересчитайте одну из них`, gone: () => 'строка товара удалена' },
    summary: { title: 'Итог', expected: 'Ожидалось', counted: 'Посчитано', scanned: 'сканов', typed: 'вручную', skipped: 'Пропущено — остатку доверяем',
               extra: 'Лишнее', missing: 'Не хватает', wrongPlace: 'Чужие товары', movedIn: 'Перенесено сюда', movedOut: 'Перенесено отсюда', duration: 'Время',
               nos: 'Бесконечный остаток (WGR) посчитано', restock: 'На полках пополнения', missingRows: 'Недостающие', extraRows: 'Лишние', skippedRows: 'Пропущено',
               movedUnscanned: 'Перенесено до сканирования',
               wrongRows: 'Чужие товары — относятся к другим секциям', foundRows: 'Найдено в другой секции', moves: 'Переносы', movesPending: 'применится при сохранении',
               movement: 'Изменилось между Стартом и Сохранением', movementOk: 'объяснено событиями', movementBad: 'не объяснено — пересчитайте этот товар',
               saved: 'Подсчёт сохранён — секция готова', preview: 'Проверьте перед сохранением', noIssues: 'Всё посчитано как ожидалось.' },
    legend: { white: 'Не посчитано', orange: 'Считается', green: 'Точно', blue: 'Больше ожидаемого', red: 'Меньше ожидаемого',
              yellow: 'Не то место', purple: 'Отсутствует', skipped: 'Пропущено — остатку доверяем', gray: 'Бесконечный остаток (WGR) / полка пополнения — просто посчитано' },
  },
};

export function inventoryText(lang: string | null | undefined): Texts {
  return T[(lang as Lang) in T ? (lang as Lang) : 'en'];
}
